/**
 * AppShell — chrome applicatif (sidebar + topbar + outlet).
 *
 * Doctrine [docs/design/frontend-ux-doctrine.md] :
 *   - Inter exclusif, brand identity stricte (KollectiviaWordmark canonique).
 *   - Palette slate + navy + brand-blue (zéro legacy).
 *   - Full-width utile : `<main>` n'enferme plus dans un cap dur ; chaque
 *     page choisit son propre cap interne (les surfaces de comparaison
 *     multi-assureurs futures consommeront `max-w-none` ou `max-w-screen-2xl`).
 *   - Responsive sérieux : sidebar fixe ≥ lg, drawer slide-in < lg accessible
 *     (focus-trap minimal, Esc, backdrop click, restore focus, body scroll lock).
 *   - Light mode Phase 1 (DETTES.md « Dark mode brand-aligned » P2 — chunk dédié futur).
 *
 * Architecture compound (vercel-composition-patterns) :
 *   - <SidebarPanel>     — desktop fixe (lg+) + drawer mobile partagent les mêmes items
 *   - <NavItems>         — liste de NavLink, factorisée + icônes + labels collapse-aware
 *   - <Topbar>           — sticky, burger button + tenant context + lang + signOut
 *   - <MobileDrawer>     — overlay portail-less, focus-trap léger, Esc, backdrop
 *
 * 9.P.3.A — sidebar collapse/expand desktop :
 *   - Bouton collapse en footer sidebar (parité Linear/Notion/Vercel).
 *   - Width collapsed = `lg:w-16` (64px) vs expanded = `lg:w-64` (256px).
 *   - Mobile drawer inchangé strict (toujours expanded en overlay).
 *   - Préférence persistée localStorage `kollectivia-sidebar-collapsed` ;
 *     hydratation lazy via initializer pour éviter SSR/SSG flash.
 *   - Default first visit = expanded (decision PO).
 *   - aria-expanded sur le bouton + aria-label bilingue + title tooltip
 *     sur les NavItems collapsed (l'icône seule reste explicite via title).
 *   - Mobile topbar = KollectiviaMark seul (mark only, no wordmark text).
 *
 * a11y :
 *   - <SkipLink> en premier enfant
 *   - <aside aria-label> sidebar landmark
 *   - <nav aria-label> primary nav
 *   - <main id="main" role="main" tabIndex={-1}> cible du skip-link
 *   - Drawer = role="dialog" + aria-modal + aria-labelledby + focus initial
 *   - Burger = aria-expanded + aria-controls
 *   - Collapse btn = aria-expanded + aria-label dynamique
 */

import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useLocation } from "react-router-dom";

import { useAuth } from "../contexts/AuthContext";
import { cn } from "../lib/cn";
import { tenantScope } from "../lib/tenantScope";

import { KollectiviaMark, KollectiviaWordmark } from "./brand/KollectiviaMark";
import { guideContext, guideHref, resumeGuideHref } from "./help/guide-context";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { NotificationBell } from "./NotificationBell";
import { SkipLink } from "./SkipLink";
import { TenantScopeSwitcher } from "./TenantScopeSwitcher";
import { VersionMenu } from "./VersionMenu";

const SIDEBAR_COLLAPSED_KEY = "kollectivia-sidebar-collapsed";

type NavItem = {
  to: string;
  labelKey: string;
  end?: boolean;
  icon: (props: { className?: string }) => JSX.Element;
};

// -----------------------------------------------------------------------------
// Icônes nav — SVG inline, stroke=currentColor, 20px viewport.
// Outline style minimal aligné brand (slate-700 default, white actif).
// -----------------------------------------------------------------------------

function IconDashboard({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <rect x="3" y="3" width="6" height="6" rx="1.25" stroke="currentColor" strokeWidth="1.5" />
      <rect x="11" y="3" width="6" height="6" rx="1.25" stroke="currentColor" strokeWidth="1.5" />
      <rect x="3" y="11" width="6" height="6" rx="1.25" stroke="currentColor" strokeWidth="1.5" />
      <rect x="11" y="11" width="6" height="6" rx="1.25" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
function IconCompanies({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M3 17V5a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v12M12 9h3a1 1 0 0 1 1 1v7M2 17h16"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M5.5 7.5h1M5.5 10.5h1M5.5 13.5h1M8.5 7.5h1M8.5 10.5h1M8.5 13.5h1"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
      />
    </svg>
  );
}
function IconInsurers({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M3 7.5 10 3l7 4.5M4 8.5v6M8 8.5v6M12 8.5v6M16 8.5v6M2.5 17h15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function IconProjects({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M2.5 6a1.5 1.5 0 0 1 1.5-1.5h3.6l1.6 1.5H16a1.5 1.5 0 0 1 1.5 1.5v7.5A1.5 1.5 0 0 1 16 16.5H4A1.5 1.5 0 0 1 2.5 15V6Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function IconIntakeLinks({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M8.5 11.5 11.5 8.5M7.5 13.5l-1 1a2.83 2.83 0 1 1-4-4l1-1m9-3 1-1a2.83 2.83 0 1 1 4 4l-1 1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
function IconBrokers({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <circle cx="7" cy="7" r="2.75" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="14" cy="8" r="2.25" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M2.5 16.5c0-2.5 2.02-4 4.5-4s4.5 1.5 4.5 4M12.5 16.5c.2-2 1.78-3 3.5-3s3.3 1 3.5 3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
function IconGuarantees({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M10 2.5 16.5 5v4.5c0 4-2.6 6.85-6.5 8-3.9-1.15-6.5-4-6.5-8V5L10 2.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="m7.5 10 1.75 1.75L13 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function IconSettings({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <circle cx="10" cy="10" r="2.25" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M10 2.5v2M10 15.5v2M2.5 10h2M15.5 10h2M4.7 4.7l1.4 1.4M13.9 13.9l1.4 1.4M4.7 15.3l1.4-1.4M13.9 6.1l1.4-1.4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
function IconAgents({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <rect x="4" y="6" width="12" height="9" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M10 3v3M7.5 9.5h.01M12.5 9.5h.01M8 12.5h4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

// 9.T.1 — unified "Nouvelle demande" entry icon (cercle + plus inscrit).
function IconNewRequest({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10 7v6M7 10h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

// F2-Forms-1c — icône « Formulaires » (document avec lignes de champ).
function IconForms({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <rect x="4" y="3" width="12" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M7 7h6M7 10h6M7 13h4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

// 9.U.7 — icône « Réponses » (bac de réception / registre reçu).
function IconResponses({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M3 11l2.2-5.4A2 2 0 017.05 4.5h5.9a2 2 0 011.85 1.1L17 11v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-3z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M3 11h4l1 2h4l1-2h4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type NavItemExt = NavItem & { superAdminOnly?: boolean };

const DAILY_NAV_ITEMS: ReadonlyArray<NavItemExt> = [
  { to: "/", labelKey: "nav.dashboard", end: true, icon: IconDashboard },
  { to: "/projects", labelKey: "nav.projects", icon: IconProjects },
  { to: "/companies", labelKey: "nav.companies", icon: IconCompanies },
  { to: "/help", labelKey: "nav.help", icon: IconForms },
];
const NAV_GROUPS = [
  {
    labelKey: "nav.receptions",
    icon: IconResponses,
    items: [
      { to: "/intake-events", labelKey: "reception_flow.receipts", icon: IconResponses, end: true },
      { to: "/reponses", labelKey: "nav.reponses", icon: IconResponses },
      { to: "/intake-links", labelKey: "nav.intake_links", icon: IconIntakeLinks },
      { to: "/intake-events/new", labelKey: "workspace.upload", icon: IconNewRequest },
    ],
  },
  {
    labelKey: "nav.library",
    icon: IconForms,
    items: [
      { to: "/formulaires", labelKey: "nav.formulaires", icon: IconForms },
      { to: "/insurers", labelKey: "nav.insurers", icon: IconInsurers },
      { to: "/guarantees", labelKey: "nav.guarantees", icon: IconGuarantees },
    ],
  },
  {
    labelKey: "nav.settings",
    icon: IconSettings,
    items: [
      { to: "/settings", labelKey: "nav.cabinet_settings", end: true, icon: IconSettings },
      { to: "/settings/team", labelKey: "nav.team", icon: IconBrokers },
      { to: "/brokers", labelKey: "nav.brokers", icon: IconBrokers },
      {
        to: "/settings/agent-identities",
        labelKey: "nav.agent_identities",
        icon: IconAgents,
        superAdminOnly: true,
      },
    ],
  },
] satisfies ReadonlyArray<{ labelKey: string; icon: NavItem["icon"]; items: NavItemExt[] }>;

// -----------------------------------------------------------------------------
// Hydratation localStorage — lazy initializer pour éviter SSR/SSG flash.
// -----------------------------------------------------------------------------

function readInitialCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { user, tenant, signOut } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const helpRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!helpOpen || !helpRef.current) return;
    const header = document.querySelector("[data-project-anchor-occluder]");
    helpRef.current.style.scrollMarginTop = `${(header?.getBoundingClientRect().height ?? 0) + 16}px`;
    helpRef.current.focus({ preventScroll: true });
    helpRef.current.scrollIntoView?.({ block: "start" });
  }, [helpOpen]);
  const [collapsed, setCollapsed] = useState<boolean>(readInitialCollapsed);
  const drawerId = useId();
  const burgerRef = useRef<HTMLButtonElement | null>(null);
  const location = useLocation();
  const helpContext = guideContext(location.pathname, location.search);
  const flushWorkspace =
    /^\/(?:projects(?:\/[^/]+(?:\/(?:comparison|scoring|report))?)?|submissions\/[^/]+|intake-events(?:\/[^/]+)?|companies|requests\/new(?:\/start)?|quote-requests\/[^/]+\/(?:preparer|parcours))$/.test(
      location.pathname,
    );

  // Drawer auto-close on navigation (parité UX commun mobile drawers).
  useEffect(() => {
    setDrawerOpen(false);
    setHelpOpen(false);
  }, [location.pathname, location.search]);

  // Persistance collapsed state (localStorage).
  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      // Storage unavailable (private mode, quota) — silent no-op.
    }
  }, [collapsed]);

  // Body scroll lock + Esc handler pendant l'ouverture du drawer.
  useEffect(() => {
    if (!drawerOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setDrawerOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [drawerOpen]);

  // Restore focus to burger when drawer closes.
  useEffect(() => {
    if (!drawerOpen) {
      // Microtask pour laisser React démonter le drawer avant le focus.
      queueMicrotask(() => burgerRef.current?.focus());
    }
  }, [drawerOpen]);

  const onOpenDrawer = useCallback(() => setDrawerOpen(true), []);
  const onCloseDrawer = useCallback(() => setDrawerOpen(false), []);
  const onToggleCollapse = useCallback(() => setCollapsed((c) => !c), []);

  const showRole = user?.role === "super_admin";

  return (
    <div className="bg-slate-50 text-slate-900 lg:flex lg:h-dvh lg:overflow-hidden">
      <SkipLink />

      {/* Sidebar desktop (≥ lg) — viewport-bound, scroll interne nav si nombreuse,
          INDÉPENDANT du scroll du main. Width = collapsed?w-16:w-64 (lg)/w-72 (2xl). */}
      <aside
        aria-label={t("nav.sidebar")}
        className={cn(
          "hidden border-r border-slate-200 bg-white motion-safe:transition-[width] motion-safe:duration-150 motion-reduce:transition-none lg:flex lg:h-dvh lg:flex-shrink-0 lg:flex-col",
          collapsed ? "lg:w-16" : "lg:w-64 2xl:lg:w-72",
        )}
      >
        <div
          className={cn(
            "flex h-16 shrink-0 items-center border-b border-slate-200",
            collapsed ? "justify-center px-0" : "px-6",
          )}
        >
          {collapsed ? (
            <KollectiviaMark size={28} ariaLabel="Kollectivia" />
          ) : (
            <KollectiviaWordmark markSize={28} textSize={17} />
          )}
        </div>
        <nav
          aria-label={t("nav.primary")}
          className={cn("flex-1 space-y-1 overflow-y-auto py-4", collapsed ? "px-2" : "px-3")}
        >
          <NavItems collapsed={collapsed} />
        </nav>
        <div className="shrink-0 border-t border-slate-200 py-2">
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-expanded={!collapsed}
            aria-label={collapsed ? t("nav.expand_sidebar") : t("nav.collapse_sidebar")}
            title={collapsed ? t("nav.expand_sidebar") : t("nav.collapse_sidebar")}
            className={cn(
              "focus-visible:ring-brand-500 flex w-full touch-manipulation items-center gap-2 rounded-md text-xs font-medium text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
              collapsed ? "justify-center px-0 py-2" : "mx-1 px-3 py-2",
            )}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
              className={cn(
                "motion-safe:transition-transform motion-reduce:transition-none",
                collapsed && "rotate-180",
              )}
            >
              <path
                d="M10 4 6 8l4 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {!collapsed && <span className="truncate">{t("nav.collapse_sidebar_label")}</span>}
          </button>
          {!collapsed && (
            <div className="mt-2 px-1">
              {/* 9.R.1 — Version cliquable expose 3 docs progress projet
                  (logged-in only car AppShell sous ProtectedShellLayout). */}
              <VersionMenu tenantName={tenant?.name ?? null} variant="sidebar-desktop" />
            </div>
          )}
        </div>
      </aside>

      {/* Colonne droite — viewport-bound desktop, scroll interne main. Sur mobile,
          flex-col natural-flow + scroll page entière (drawer overlay indépendant). */}
      <div className="flex min-h-dvh min-w-0 flex-col lg:h-dvh lg:min-h-0 lg:flex-1 lg:overflow-hidden">
        <header
          data-project-anchor-occluder
          className="sticky top-0 z-20 flex shrink-0 flex-wrap items-center justify-between gap-y-2 border-b border-slate-200 bg-white px-4 py-2 lg:h-20 lg:flex-nowrap lg:px-8 lg:py-0"
        >
          <div className="flex w-full min-w-0 items-center gap-3 lg:w-auto">
            <button
              ref={burgerRef}
              type="button"
              aria-label={t("nav.open_menu")}
              aria-expanded={drawerOpen}
              aria-controls={drawerId}
              onClick={onOpenDrawer}
              className="focus-visible:ring-brand-500 inline-flex h-11 w-11 touch-manipulation items-center justify-center rounded-md text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 lg:hidden"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path
                  d="M3 6h14M3 10h14M3 14h14"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            {/* Mobile : KollectiviaMark seul (mark only, no wordmark text — décision PO 9.P.3.A). */}
            <div className="lg:hidden">
              <KollectiviaMark size={28} ariaLabel="Kollectivia" />
            </div>
            {/* Desktop : tenant context.
                9.AS.1 — un super_admin n'appartient à aucun cabinet : le libellé
                afficherait « — » en permanence. Il choisit donc le cabinet qu'il
                regarde, et cette portée part sur toutes les requêtes. */}
            <div className="min-w-0 flex-1 lg:flex-none">
              <TenantScopeSwitcher />
            </div>
          </div>

          <div className="flex w-full min-w-0 flex-wrap items-center justify-end gap-2 text-sm sm:gap-3 lg:w-auto">
            <Link
              to="/requests/new"
              aria-label={t("nav.new_request")}
              title={t("nav.new_request")}
              className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-2 rounded-lg px-2 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 xl:px-3"
            >
              <IconNewRequest className="h-5 w-5" />
              <span className="hidden xl:inline">{t("nav.new_request")}</span>
            </Link>
            <span className="hidden max-w-48 truncate text-slate-600 xl:inline">
              {user?.full_name ?? user?.email}
              {showRole && user?.role && (
                <>
                  <span aria-hidden="true" className="text-slate-300">
                    {" "}
                    ·{" "}
                  </span>
                  <span className="text-slate-500" translate="no">
                    {user.role}
                  </span>
                </>
              )}
            </span>
            <button
              type="button"
              aria-label={t(helpOpen ? "guide.close" : "guide.open")}
              aria-expanded={helpOpen}
              aria-controls="contextual-help"
              onClick={() => setHelpOpen((open) => !open)}
              className="text-brand-700 focus-visible:ring-brand-500 inline-flex min-h-11 min-w-11 items-center justify-center rounded-full border border-slate-300 font-semibold focus-visible:outline-none focus-visible:ring-2"
            >
              ?
            </button>
            <NotificationBell />
            <LanguageSwitcher className="[&>button]:min-h-11 [&>button]:min-w-11" />
            <button
              type="button"
              aria-label={t("nav.logout")}
              title={t("nav.logout")}
              onClick={() => void signOut()}
              className="focus-visible:ring-brand-500 inline-flex min-h-11 min-w-11 touch-manipulation items-center justify-center rounded-md px-2 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              <svg
                className="h-5 w-5 md:hidden"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                aria-hidden="true"
              >
                <path d="M9 4H4v16h5M13 8l4 4-4 4m-5-4h13" />
              </svg>
              <span className="hidden md:inline">{t("nav.logout")}</span>
            </button>
          </div>
        </header>

        {/* Contain absolute sr-only descendants inside the scrollport, not the document. */}
        <main
          id="main"
          role="main"
          tabIndex={-1}
          className="relative min-h-0 flex-1 px-4 focus-visible:outline-none lg:overflow-y-auto lg:px-8"
        >
          {/* Spacing belongs to content, not the scrollport: sticky top-0 must meet its edge. */}
          <div className={flushWorkspace ? "py-0" : "py-8 lg:py-10 print:py-0"}>
            {helpOpen && (
              <aside
                id="contextual-help"
                ref={helpRef}
                tabIndex={-1}
                aria-label={t("guide.open")}
                className="border-brand-200 bg-brand-50 my-4 space-y-2 rounded-lg border p-3 print:hidden"
              >
                <p className="text-sm text-slate-700">
                  {t(
                    location.pathname === "/settings/team"
                      ? "guide.team_context"
                      : "guide.context_summary",
                  )}
                </p>
                <Link
                  className="text-brand-700 focus-visible:ring-brand-500 inline-flex min-h-11 items-center underline focus-visible:ring-2"
                  state={{
                    returnTo: location.pathname + location.search + location.hash,
                    userId: user?.id,
                    tenantId: tenantScope(),
                  }}
                  to={
                    helpContext.projectId || helpContext.requestId || helpContext.companyId
                      ? guideHref(
                          helpContext.projectId,
                          helpContext.requestId,
                          helpContext.companyId,
                        )
                      : resumeGuideHref(user?.id, tenantScope())
                  }
                >
                  {t("guide.resume")}
                </Link>
              </aside>
            )}
            {children}
          </div>
        </main>
      </div>

      {/* Mobile drawer overlay (visible <lg quand drawerOpen) */}
      {drawerOpen && (
        <MobileDrawer id={drawerId} onClose={onCloseDrawer} tenantName={tenant?.name ?? null} />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sous-composants
// -----------------------------------------------------------------------------

function NavItems({
  onNavigate,
  collapsed = false,
}: {
  onNavigate?: () => void;
  collapsed?: boolean;
} = {}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isSuperAdmin = user?.role === "super_admin";
  const renderItem = (item: NavItemExt) => {
    const label = t(item.labelKey);
    const Icon = item.icon;
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        onClick={onNavigate}
        aria-label={collapsed ? label : undefined}
        title={collapsed ? label : undefined}
        className={({ isActive }) =>
          cn(
            "focus-visible:ring-brand-500 flex min-h-11 touch-manipulation items-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 motion-reduce:transition-none",
            collapsed ? "w-10 justify-center" : "gap-3 px-3 py-2",
            isActive ? "bg-navy-950 text-white" : "text-slate-700 hover:bg-slate-100",
          )
        }
      >
        <Icon className="h-5 w-5 shrink-0" />
        {!collapsed && <span className="min-w-0 break-words">{label}</span>}
      </NavLink>
    );
  };

  return (
    <>
      {DAILY_NAV_ITEMS.map(renderItem)}
      {NAV_GROUPS.map((group) => {
        const items = group.items.filter(
          (item: NavItemExt) => !item.superAdminOnly || isSuperAdmin,
        );
        const active = items.some((item: NavItemExt) =>
          item.end
            ? pathname === item.to
            : pathname === item.to || pathname.startsWith(`${item.to}/`),
        );
        return (
          <NavigationGroup
            key={`${group.labelKey}:${pathname}`}
            label={t(group.labelKey)}
            icon={group.icon}
            active={active}
            collapsed={collapsed}
          >
            {items.map(renderItem)}
          </NavigationGroup>
        );
      })}
    </>
  );
}

function NavigationGroup({
  label,
  icon: Icon,
  active,
  collapsed,
  children,
}: {
  label: string;
  icon: NavItem["icon"];
  active: boolean;
  collapsed: boolean;
  children: ReactNode;
}) {
  const id = useId();
  const [open, setOpen] = useState(active);
  return (
    <div className="mt-3 border-t border-slate-200 pt-2">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        aria-label={label}
        title={collapsed ? label : undefined}
        onClick={() => setOpen(!open)}
        className={cn(
          "focus-visible:ring-brand-500 flex min-h-11 w-full items-center rounded-md text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2",
          collapsed ? "justify-center" : "gap-3 px-3 py-2",
          active && "bg-slate-100",
        )}
      >
        <Icon className="h-5 w-5 shrink-0" />
        {!collapsed && (
          <>
            <span className="flex-1 text-left">{label}</span>
            <span aria-hidden="true">{open ? "−" : "+"}</span>
          </>
        )}
      </button>
      <div
        id={id}
        hidden={!open}
        className={cn("mt-1 space-y-1", !collapsed && "ml-3 border-l border-slate-200 pl-2")}
      >
        {children}
      </div>
    </div>
  );
}

function MobileDrawer({
  id,
  onClose,
  tenantName,
}: {
  id: string;
  onClose: () => void;
  tenantName: string | null;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const firstNavRef = useRef<HTMLDivElement | null>(null);

  // Focus initial — premier item nav.
  useEffect(() => {
    const node = firstNavRef.current;
    if (!node) return;
    const firstFocusable = node.querySelector<HTMLElement>("a,button");
    queueMicrotask(() => firstFocusable?.focus());
  }, []);

  return (
    <div
      id={id}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-30 lg:hidden"
    >
      <div
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
      />
      <div
        className="motion-safe:animate-slide-in-left relative z-10 flex h-full w-72 max-w-[85vw] flex-col border-r border-slate-200 bg-white shadow-xl"
        ref={firstNavRef}
      >
        <div className="flex h-16 items-center justify-between border-b border-slate-200 px-5">
          <h2 id={titleId} className="sr-only">
            {t("nav.primary")}
          </h2>
          <KollectiviaWordmark markSize={24} textSize={16} />
          <button
            type="button"
            onClick={onClose}
            aria-label={t("nav.close_menu")}
            className="focus-visible:ring-brand-500 inline-flex h-9 w-9 touch-manipulation items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path
                d="M5 5l8 8M13 5l-8 8"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <nav aria-label={t("nav.primary")} className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {/* Mobile drawer toujours expanded (full labels + icônes). */}
          <NavItems onNavigate={onClose} collapsed={false} />
        </nav>
        <div className="border-t border-slate-200 px-2 py-2">
          {/* 9.R.1 — Version cliquable mobile (parité desktop). */}
          <VersionMenu tenantName={tenantName} variant="drawer-mobile" />
        </div>
      </div>
    </div>
  );
}
